import { createContext, MutableRefObject, useCallback, useContext } from 'react';
import { Socket } from 'socket.io-client';
import { ReactChildren } from 'src/shared/types/util.types';
import { useSocketConnection } from 'src/shared/hooks/useSocketConnection';
import { useSocketListeners } from 'src/shared/hooks/useSocketListeners';
import { useSocketSubscriptions } from 'src/shared/hooks/useSocketSubscriptions';

interface useSocketProviderProps {
  socketReady: boolean;
  socket: Socket | null;
  addListener: (
    room: string,
    eventName: string,
    callbackId: string,
    callback: MutableRefObject<(data: unknown) => void>
  ) => void;
  removeListener: (room: string, eventName: string, callbackId: string) => void;
  emit: (room: string, eventName: string, data: any) => void;
}

const SocketContext = createContext<useSocketProviderProps>({
  socketReady: false,
  socket: null,
  addListener: () => {
    throw new Error('Socket context not ready');
  },
  removeListener: () => {
    throw new Error('Socket context not ready');
  },
  emit: () => {
    throw new Error('Socket context not ready');
  },
});

export function useSocketProvider() {
  return useContext(SocketContext);
}

export function SocketProvider({ children }: ReactChildren) {
  // Manage socket connection lifecycle (creation, connection, disconnection)
  const { socket, socketReady } = useSocketConnection();

  // Manage event listeners and subscriptions state
  const { subscriptions, addListener, removeListener } = useSocketListeners(socket, socketReady);

  // Handle server-side room subscriptions/unsubscriptions
  useSocketSubscriptions(socket, socketReady, subscriptions);

  // Emit events through the socket
  const emit = useCallback(
    (room: string, eventName: string, data: any) => {
      if (!socket) {
        return;
      }
      socket.emit(`<${room}>:${eventName}`, data);
    },
    [socket]
  );

  const value: useSocketProviderProps = {
    socketReady: socketReady ?? false,
    socket,
    addListener,
    removeListener,
    emit,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
